import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, Typography, Box } from '@material-ui/core';

import { useTypedTranslation } from '@app/hooks';
import { ModalComponent, PasslyButton } from '@app/components';
import { genericErrorString, hasOrgPermission } from '@app/helpers';
import { agentsService } from '@app/services';
import { AddPermissionsToOauth2 } from '../dialogs/addPermissionsToOauth2.dialog';
import { Administrator } from '@app/store/constants';
import { IClientRoles } from '@app/models';

interface IProps {
    clientId: any;
    clientRoles: IClientRoles[];
    formValidCallback: Function;
}

const Oauth2Config: React.SFC<IProps> = ({ clientId, clientRoles, formValidCallback }) => {
    const shellConfig: any = useSelector((state: any) => state.basic.shellConfig);

    const [permissions, setPermissions] = useState<IClientRoles[]>(clientRoles);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { translation } = useTypedTranslation();

    useEffect(() => {
        formValidCallback(true);
    }, []);

    const addPermissions = () => {
        setOpenModal(true);
    };

    const handleModalCloseCallback = () => {
        setOpenModal(!openModal);
    };

    function getPermissions() {
        handleModalCloseCallback();

        agentsService.getClientPermissions(clientId, getClientPermissionsSuccess, handleError);
    }

    const getClientPermissionsSuccess = (response: any) => {
        setPermissions(response.data.value as IClientRoles[]);
    };

    const resolveOrgName = (item: any) => {
        if (item && item.OrganizationId) {
            const orgs: any = shellConfig.organizations;

            for (let org of orgs) {
                if (org.id === item.OrganizationId) {
                    return org.name;
                }
            }
        }

        return null;
    };

    const removeClientPermission = (roleId: string) => {
        agentsService.removeClientPermission(clientId, roleId, removeClientPermissionSuccess.bind(null, roleId), handleError);
    };

    const removeClientPermissionSuccess = (roleId: string) => {
        let filteredRows = permissions.filter((permission) => {
            return permission.Id !== roleId;
        });

        setPermissions(filteredRows);
    };

    const handleError = (error: any) => {
        setErrorMessage(genericErrorString(error));
    };

    return (
        <>
            <ModalComponent
                headerTitle={translation('ADD_PERMISSIONS')}
                form={<AddPermissionsToOauth2 clientId={clientId} onPermissionsAdded={getPermissions} toggleModal={handleModalCloseCallback} />}
                modalSwitcherCallback={handleModalCloseCallback}
                localModalSwitcher={openModal}
            />
            <div className='rowcontainer'>
                <div className='configpanel-left configpanel-left--ouath-config'>
                    <Box ml={2}>
                        <Typography variant='h3'>{translation('PERMISSIONS')}</Typography>
                    </Box>
                    {permissions.length > 0 &&
                        <span className='detailPage__list--secondary-label'>
                            <ul className='detailPage__secondary-list'>
                                {permissions.map((item: any, index: number) => (
                                    <li key={index}>
                                        <span>{item.Name}</span>
                                        <span className='detailPage__item-highlight'>({resolveOrgName(item)})</span>
                                        {hasOrgPermission([Administrator]) &&
                                            <span id={`removeClientPermission${index}`}>
                                                (<Link onClick={() => removeClientPermission(item.Id)}>{translation('REMOVE')}</Link>)
                                        </span>
                                        }
                                    </li>
                                ))}
                            </ul>
                        </span>
                    }
                    {errorMessage !== '' &&
                        <span className='modal__error'>{errorMessage}</span>
                    }
                </div>
                <div className='configpanel-right configpanel-right--ouath-config'>
                    <div dangerouslySetInnerHTML={{ __html: translation('OAUTH2_CLIENT_CONFIG_HELP') }} />
                    <PasslyButton id='addClientPermissions' variant='contained' color='primary' onClick={addPermissions}>{translation('ADD_PERMISSIONS')}</PasslyButton>
                </div>
            </div>
        </>
    );
};

export default Oauth2Config;